import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { toast } from 'sonner';
import { Wallet, Calendar, Clock, CheckCircle2, FileText, Euro, XCircle, ExternalLink, TrendingUp } from 'lucide-react';

import { api } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ExportButtons } from './AgentCommissionsAdminPage';
import MonthlyIncomeCard from '../components/MonthlyIncomeCard';

const STATUS_META = {
  pending:   { labelKey: 'myCommissions.stPending', labelDefault: 'In attesa',  className: 'bg-amber-100 text-amber-800 border-amber-200',   icon: Clock },
  unlocked:  { labelKey: 'myCommissions.stUnlocked', labelDefault: 'Sbloccata',  className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  invoiced:  { labelKey: 'myCommissions.stInvoiced', labelDefault: 'Fatturata',  className: 'bg-blue-100 text-blue-800 border-blue-200',       icon: FileText },
  paid:      { labelKey: 'myCommissions.stPaid', labelDefault: 'Incassata',  className: 'bg-violet-100 text-violet-800 border-violet-200', icon: Euro },
  cancelled: { labelKey: 'myCommissions.stCancelled', labelDefault: 'Annullata',  className: 'bg-slate-100 text-slate-600 border-slate-200',    icon: XCircle },
};

const dateLocale = () => {
  const lng = i18n.language || 'it';
  if (lng.startsWith('es')) return 'es-ES';
  if (lng.startsWith('en')) return 'en-GB';
  return 'it-IT';
};

const fmtEUR = (v) => new Intl.NumberFormat(dateLocale(), { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(Number(v || 0));
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(dateLocale(), { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

const buildPreset = (key) => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const iso = (d) => d.toISOString().slice(0, 10);
  switch (key) {
    case 'this_month':
      return { from: iso(new Date(Date.UTC(y, m, 1))), to: iso(new Date(Date.UTC(y, m + 1, 0))) };
    case 'last_month':
      return { from: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) };
    case 'this_year':
      return { from: iso(new Date(Date.UTC(y, 0, 1))), to: iso(new Date(Date.UTC(y, 11, 31))) };
    case 'all':
    default:
      return { from: null, to: null };
  }
};

export default function MyCommissionsPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('this_year');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const { from, to } = useMemo(() => buildPreset(period), [period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const { data } = await api.get('/agent-commissions/mine', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('myCommissions.loadError', 'Errore caricamento commissioni'));
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="my-commissions-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{t('myCommissions.eyebrow', 'Le mie')}</div>
          <h1 className="font-serif text-2xl md:text-3xl inline-flex items-center gap-2">
            <Wallet className="w-6 h-6 text-[var(--gp-gold)]" />
            {t('myCommissions.title', 'Commissioni')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('myCommissions.subtitle', 'Elenco delle tue commissioni con data di sblocco fatturazione.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            variant="mine"
            filters={{
              ...(from && { from_date: from }),
              ...(to && { to_date: to }),
            }}
          />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[170px] h-10" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">{t('myCommissions.thisMonth', 'Questo mese')}</SelectItem>
              <SelectItem value="last_month">{t('myCommissions.lastMonth', 'Mese scorso')}</SelectItem>
              <SelectItem value="this_year">{t('myCommissions.thisYear', 'Anno corrente')}</SelectItem>
              <SelectItem value="all">{t('myCommissions.all', 'Tutte')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ★ 2026-08-14 — Incasso del Mese + Fatture in scadenza (summary in cima) */}
      <MonthlyIncomeCard />

      {/* KPI */}
      <Card className="p-4 bg-gradient-to-r from-[var(--gp-gold)]/10 to-[var(--gp-gold)]/2 border-[var(--gp-gold)]/30">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3 text-[var(--gp-gold)]" />
              {t('myCommissions.totalInPeriod', 'Totale nel periodo selezionato')}
            </div>
            <div className="font-serif text-3xl md:text-4xl text-[var(--gp-gold)] tabular-nums mt-1" data-testid="mycomm-total">
              {fmtEUR(total)}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">{items.length} {t('myCommissions.commissionsCount', 'commissioni')}</div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)]/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left py-2.5 px-3">{t('myCommissions.thClosing', 'Chiusura')}</th>
                <th className="text-right py-2.5 px-3">{t('myCommissions.thAmount', 'Importo')}</th>
                <th className="text-center py-2.5 px-3">{t('myCommissions.thUnlock', 'Sblocco')}</th>
                <th className="text-center py-2.5 px-3">{t('myCommissions.thStatus', 'Stato')}</th>
                <th className="text-left py-2.5 px-3">{t('myCommissions.thNotes', 'Note')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('myCommissions.loading', 'Caricamento…')}</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <Wallet className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <div className="text-sm text-muted-foreground">{t('myCommissions.emptyPeriod', 'Nessuna commissione nel periodo selezionato.')}</div>
                  </td>
                </tr>
              )}
              {items.map((row) => {
                const s = STATUS_META[row.status] || STATUS_META.pending;
                const StatusIcon = s.icon;
                const pp = row.proposal_payment || {};
                return (
                  <tr key={row.id} className="border-t border-border hover:bg-[var(--surface-2)]/30" data-testid={`row-${row.id}`}>
                    <td className="py-3 px-3">
                      <div className="font-medium">{pp.lead_name || '—'}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <span>{fmtEUR(pp.amount_eur || 0)}</span>
                        {pp.resort_slug && <Badge variant="outline" className="text-[10px] py-0 h-4">{pp.resort_slug}</Badge>}
                        {pp.lead_id && (
                          <Link to={`/app/leads/${pp.lead_id}`} className="text-[hsl(var(--primary))] hover:underline inline-flex items-center">
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-right tabular-nums">
                      <div className="font-medium">{fmtEUR(row.amount)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {row.amount_type === 'percent' ? `${row.amount_percent}%` : t('myCommissions.fixed', 'Fisso')}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="inline-flex items-center gap-1 text-sm">
                        <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                        {fmtDate(row.unlock_date)}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Badge className={`border ${s.className}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {t(s.labelKey, s.labelDefault)}
                      </Badge>
                    </td>
                    <td className="py-3 px-3 text-xs text-muted-foreground">{row.notes || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
